/**
* functions common hash
*
* @category   Javascript
* @author     Jakob Ohlsen <jakob.ohlsen@gmail.com>
* @copyright  2025 Jakob Ohlsen
*/
// MurmurHash3 constants
const C1 = 0xcc9e2d51;
const C2 = 0x1b873593;
const C3 = 0xe6546b64;
const C4 = 0x85ebca6b;
const C5 = 0xc2b2ae35;
// Helper functions to perform bitwise operations on 32-bit integers
function rotl32(x, r) {
    return (x << r) | (x >>> (32 - r));
}
function fmix32(h) {
    h ^= h >>> 16;
    h = Math.imul(h, C4);
    h ^= h >>> 13;
    h = Math.imul(h, C5);
    h ^= h >>> 16;
    return h;
}
/**
 * MurmurHash3 (x86 32-bit) over the **low byte of each UTF-16 code unit** of
 * `key`. Returns the resulting 32-bit hash as a base-36 string.
 *
 * ## ⚠️ ASCII-only contract
 *
 * This implementation reads input as bytes via `charCodeAt(i) & 0xff`, which
 * discards the high byte of every UTF-16 code unit. For ASCII input (≤ 0x7f)
 * this exactly matches the canonical MurmurHash3-x86_32 byte interpretation.
 * For non-ASCII input it **collapses different codepoints onto the same hash**:
 *
 *   - Cyrillic 'А' (U+0410) hashes the same as DLE control char (U+0010).
 *   - Latin 'A' (U+0041) hashes the same as 'Ł' (U+0141).
 *
 * Acceptable use cases:
 *   - Compaction of mostly-ASCII fingerprint data, where collisions among
 *     real users at 32-bit output space are already at the 1/2³² floor.
 *
 * **Do NOT use** for:
 *   - Content-identity / dedup hashing over user-supplied unicode strings.
 *   - Any context where two distinct unicode inputs MUST map to distinct
 *     hash outputs.
 *
 * For UTF-8-correct hashing, encode first:
 *   `murmurHash3Ascii(new TextDecoder().decode(new TextEncoder().encode(key)))`
 * — or better, replace this implementation with one that iterates over the
 * bytes produced by `new TextEncoder().encode(key)`.
 */
export function murmurHash3Ascii(key, seed = 0) {
    let h = seed;
    let k = 0;
    let remainder = key.length & 3; // key.length % 4
    let bytes = key.length - remainder;
    let i = 0;
    // Process key in 4-byte chunks
    while (i < bytes) {
        k =
            (key.charCodeAt(i) & 0xff) |
                ((key.charCodeAt(++i) & 0xff) << 8) |
                ((key.charCodeAt(++i) & 0xff) << 16) |
                ((key.charCodeAt(++i) & 0xff) << 24);
        ++i;
        // Mix k into h
        k = Math.imul(k, C1);
        k = rotl32(k, 15);
        k = Math.imul(k, C2);
        h ^= k;
        h = rotl32(h, 13);
        h = Math.imul(h, 5) + C3;
    }
    // Process remaining bytes
    k = 0;
    switch (remainder) {
        case 3:
            k ^= (key.charCodeAt(i + 2) & 0xff) << 16;
        case 2:
            k ^= (key.charCodeAt(i + 1) & 0xff) << 8;
        case 1:
            k ^= key.charCodeAt(i) & 0xff;
            k = Math.imul(k, C1);
            k = rotl32(k, 15);
            k = Math.imul(k, C2);
            h ^= k;
    }
    // Finalize and return
    h ^= key.length;
    h = fmix32(h);
    const v = h >>> 0; // Convert to unsigned 32-bit integer
    return v.toString(36); // Convert to base-36 string
}
// export async function cryptoHash(key: string): Promise<string> {
//   const data = new TextEncoder().encode(key);
//   const digest = await crypto.subtle.digest('SHA-256', data);
//   const bytes = new Uint8Array(digest);
//   let hex = '';
//   for (let i = 0; i < bytes.length; i++) {
//     hex += bytes[i].toString(16).padStart(2, '0');
//   }
//   return hex;
// }
